import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {  trackEvent } from "../../utils/google-analytics";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../styles/PagesStyles/Home.css";
// import logos
import LogoBlack from "../../assets/images/LogoCombatSolutionsBlack.png";
import LogoWhite from "../../assets/images/LogoCombatSolutionsWhite.png";
// Import images
import aws from "../../assets/images/tools/aws.png";
import chatgpt from "../../assets/images/tools/chatgpt.png";
import apacheAirflow from "../../assets/images/tools/devicon_apacheairflow.png";
import gemini from "../../assets/images/tools/gemini.png";
import huggingFace from "../../assets/images/tools/hf.png";
import kafka from "../../assets/images/tools/kafka.png";
import langchain from "../../assets/images/tools/langchain.png";
import llama from "../../assets/images/tools/llama.png";
import meta from "../../assets/images/tools/meta.png";
import mistral from "../../assets/images/tools/mistral-ai.png";
import n8n from "../../assets/images/tools/n8n.png";
import pytorch from "../../assets/images/tools/pytorch.png";
import tensorflow from "../../assets/images/tools/tf.png";

// Partners
import aalpha from "../../assets/images/partners/aalpha.png";
import ashoka from "../../assets/images/partners/ashoka.png";
import besa from "../../assets/images/partners/besa.png";
import entelki from "../../assets/images/partners/entelki.png";
import hoonartek from "../../assets/images/partners/hoonartek.png";
import mammoth from "../../assets/images/partners/mammoth.png";
import mfpa from "../../assets/images/partners/mfpa.png";
import nabni from "../../assets/images/partners/nabni.png";
import octalframes from "../../assets/images/partners/octalframes.png";
import pinzak from "../../assets/images/partners/pinzak.png";
import radiall from "../../assets/images/partners/radiall.png";
import tot from "../../assets/images/partners/TOT.png";
import ship from "../../assets/images/partners/Shipbuilders.png";
import navy from "../../assets/images/partners/Navy.png";
import electronics from "../../assets/images/partners/Bharat.png";
// ai-driven
import ImgA from "../../assets/images/Ai-driven/ImgA.jpg";
import ImgB from "../../assets/images/Ai-driven/ImgB.jpg";
import ImgC from "../../assets/images/Ai-driven/ImgC.jpg";
import ImgD from "../../assets/images/Ai-driven/ImgD.jpg";


import usePageTitle from "../../hooks/usePageTitle";
import HelmetWrapper from "../../helmet";


gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  usePageTitle("Combat Solutions AI");
  const heroRef = useRef(null);
  const iconSliderRef = useRef(null);
  const partnersSliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const testimonialsRef = useRef(null);
  const slideTrackRef = useRef(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const autoSlideIntervalRef = useRef(null);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      // Clear any existing interval when screen size changes
      if (autoSlideIntervalRef.current) {
        clearInterval(autoSlideIntervalRef.current);
        autoSlideIntervalRef.current = null;
      }

      // Start auto-slide only on desktop
      if (!mobile && totalSlides > 1) {
        startAutoSlide();
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
      stopAutoSlide();
    };
  }, []);

  useEffect(() => {
    if (!partnersSliderRef.current) return;

    let fired = false;

    const handleHover = () => {
      if (fired) return;
      fired = true;

      trackEvent("partner_hover", {
        section: "partners",
      });
    };

    partnersSliderRef.current.addEventListener("mouseenter", handleHover);

    return () => {
      partnersSliderRef.current?.removeEventListener("mouseenter", handleHover);
    };
  }, []);


  // Static data arrays
  const heroIcons = [
    aws, chatgpt, apacheAirflow, gemini, huggingFace, kafka,
    langchain, llama, meta, mistral, n8n, pytorch, tensorflow
  ];

  const partnersLogos = [
    tot, mfpa, besa, entelki, pinzak, mammoth, ashoka,
    hoonartek, nabni, octalframes, radiall, aalpha,ship,navy,electronics
  ];

  // Testimonials data
  const testimonials = [
    {
      id: 1,
      rating: 5,
      text: "From design to launch, Combat Solutions handled everything very well. The team was dedicated and detail oriented. They understood our requirements and provided optimum solutions. The team also handled a few critical technical issues very well. Special shout out to Akash Varde for ensuring every technical aspect was handled perfectly.",
      name: "Rujuta Bhagwat",
      title: "Director",
      company: "Entelki Systems & Solutions Pvt. Ltd.",
    
    },
    {
      id: 2,
      rating: 5,
      text: "Working with the Combat team was seamless. They understood our requirements well and delivered AI-driven solutions on time with strong technical expertise.",
      name: "Gopinath Nadig",
      title: "Director",
      company: "Octacore Labs Pvt Ltd Bangalore",
    },
     {
      id: 3,
      rating: 5,
      text: "Combat Solutions has been an exceptional development partner for Altiv.AI. Their strong technical expertise, attention to detail, and ability to deliver robust software solutions have been critical to our success. They consistently go above and beyond to meet our needs, turning complex requirements into seamless, high-performing applications. We couldn’t ask for a more reliable and capable software development team.",
      name: "Varun Dhamija",
      title: "Founder",
      company: "Altiv.AI",
    }
  ];

  // Calculate slides based on device
// Slides are always based on pairs (desktop)
const totalSlides = Math.ceil(testimonials.length / 2);

// How many cards in THIS slide (desktop only)
const getCardsInSlide = (slideIndex) => {
  if (isMobile) return 1;

  const remaining = testimonials.length - slideIndex * 2;
  return remaining === 1 ? 1 : 2;
};


  // Start auto-slide for desktop
  const startAutoSlide = () => {
    if (isMobile || totalSlides <= 1) return;

    stopAutoSlide(); // Clear any existing interval

    autoSlideIntervalRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 5000);
  };

  // Stop auto-slide
  const stopAutoSlide = () => {
    if (autoSlideIntervalRef.current) {
      clearInterval(autoSlideIntervalRef.current);
      autoSlideIntervalRef.current = null;
    }
  };

  // Pause auto-slide on hover (desktop only)
  const handleMouseEnter = () => {
    if (!isMobile) {
      stopAutoSlide();
    }
  };

  // Resume auto-slide when mouse leaves (desktop only)
  const handleMouseLeave = () => {
    if (!isMobile) {
      startAutoSlide();
    }
  };

  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide(prev => {
      const next = (prev + 1) % totalSlides;

      trackEvent("testimonial_slide_change", {
        slide_number: next + 1,
        device: isMobile ? "mobile" : "desktop",
      });

      return next;
    });
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isMobile) return;
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!isMobile || touchStartX === null || touchEndX === null) return;

    const touchDiff = touchStartX - touchEndX;
    const minSwipeDistance = 50; // Minimum distance for a swipe

    if (Math.abs(touchDiff) > minSwipeDistance) {
      if (touchDiff > 0) {
        // Swipe left - go to next slide
        nextSlide();
      } else {
        // Swipe right - go to previous slide
        prevSlide();
      }
    }

    // Reset touch coordinates
    setTouchStartX(null);
    setTouchEndX(null);
  };

  // Update slide position with animation
  useEffect(() => {
    if (!testimonialsRef.current || !slideTrackRef.current) return;

    const slideWidth = testimonialsRef.current.offsetWidth;
const translateX = -currentSlide * slideWidth;


    gsap.to(slideTrackRef.current, {
      x: translateX,
      duration: 0.5,
      ease: "power2.out"
    });

  }, [currentSlide, isMobile]);

  // Start auto-slide when component mounts (desktop only)
  useEffect(() => {
    if (!isMobile && totalSlides > 1) {
      startAutoSlide();
    }

    return () => {
      stopAutoSlide();
    };
  }, [isMobile, totalSlides]);

  // Optimized slider animation
  const createSliderAnimation = (element, duration = 40) => {
    if (!element) return;

    const contentWidth = element.scrollWidth;
    const containerWidth = element.parentElement.offsetWidth;

    // Only animate if content is wider than container
    if (contentWidth <= containerWidth) return;

    const animation = gsap.to(element, {
      x: `-=${contentWidth / 2}`,
      duration,
      ease: "none",
      repeat: -1,
      modifiers: {
        x: gsap.utils.unitize(x => parseFloat(x) % (contentWidth / 2))
      }
    });

    // Pause on hover
    element.addEventListener("mouseenter", () => {
      animation.pause();
    });

    element.addEventListener("mouseleave", () => {
      animation.resume();
    });
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      /* =========================
         HERO INTRO
      ========================= */
      gsap.from(".hero-title", {
        opacity: 0,
        y: 40,
        duration: 1,
        ease: "power3.out",
      });

      gsap.from(".hero-subtitle", {
        opacity: 0,
        y: 30,
        duration: 1,
        delay: 0.2,
        ease: "power3.out",
      });

      gsap.from(".get-in-touch", {
        opacity: 0,
        y: 20,
        duration: 1,
        delay: 0.4,
        ease: "power3.out",
      });

      /* =========================
         CONTINUOUS SLIDERS - SIMPLIFIED
      ========================= */
      // Use setTimeout to ensure refs are available
      setTimeout(() => {
        createSliderAnimation(iconSliderRef.current, 35);
        createSliderAnimation(partnersSliderRef.current, 45);
      }, 100);

      /* =========================
         SCROLL REVEALS - REDUCED
      ========================= */
      // Only animate main sections, not every card
      const mainSections = document.querySelectorAll('.fade-in-section');
      const AiSections = document.querySelector('.ai-section-fade-in');
      mainSections.forEach((section, i) => {
        gsap.from(section, {
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
          opacity: 0,
          y: 40,
          duration: 0.8,
          delay: i * 0.1,
          ease: "power2.out",
        });
      });

      if (AiSections) {
        gsap.from(AiSections, {
          scrollTrigger: {
            trigger: AiSections,
            start: "top 97%",
            toggleActions: "play none none reverse",
          },
          opacity: 0,
          y: 40,
          duration: 0.8,
          ease: "power2.out",
        });
      }

      /* =========================
         CARD HOVER - SIMPLIFIED
      ========================= */
      // Use CSS for hover effects instead of GSAP for performance
      const cards = document.querySelectorAll(".capability-card, .solution-card");
      cards.forEach((card) => {
        // Just add a class, let CSS handle the animation
        card.addEventListener("mouseenter", () => {
          card.style.transform = "translateY(-5px)";
        });
        card.addEventListener("mouseleave", () => {
          card.style.transform = "translateY(0)";
        });
      });

    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    
    <div className="homepage" ref={heroRef}>
      <HelmetWrapper title="Page Title" description="Page desc" />

      {/* ================= HERO ================= */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-content-holder">
<h1 className="hero-title" style={{ color: "white" }}>
  Build Enterprise-Grade <span className="ai-highlight">Generative AI</span> Systems That Scale
</h1>

<p className="hero-subtitle">
  From Intelligent document understanding to autonomous AI agents and
  end-to-end workflow automation.
</p>

            <Link to="/contact" className="get-in-touch" style={{textDecoration:"none"}}>
              Get in Touch <i className="bi bi-arrow-right"></i>
            </Link>
          </div>
        </div>

        {/* ICON SLIDER */}
        <div className="icons-slider-wrapper">
          <div className="icons-slider" ref={iconSliderRef}>
            {[...heroIcons, ...heroIcons].map((icon, i) => (
              <div className="slider-icon" key={`icon-${i}`}>
                <img
                  src={icon}
                  alt="tech icon"
                  loading="lazy"
                  width="40"
                  height="40"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= GEN AI ================= */}
      <section className="gen-ai-section fade-in-section">
        <div className="gen-ai-container">
<h2>Engineering Intelligent Systems Powered by Generative AI</h2>
<p className="genai-text">
  We help organizations move beyond experimentation by building secure, compliant,
  and scalable GenAI solutions that deliver long-term business value.
</p>
          <Link to="/genai-lab" className="explore-button" style={{textDecoration:"none"}}>
            Explore Gen AI Lab <i className="bi bi-arrow-right"></i>
          </Link>
          <p className="hero-trust-text">
  Trusted by teams across finance, infrastructure, and enterprise operations.
</p>
        </div>
      </section>

      {/* ================= CAPABILITIES ================= */}
      <section className="intelligent-systems fade-in-section">
        <div className="container">
          <h2>Intelligent Systems & Capabilities</h2>
          <p>
            Powering businesses with Generative AI, intelligent agents, and scalable automation that transform workflows.
          </p>

          <div className="home-capabilities-grid">
            {[
              ["bi-robot", "Chatbot & Multi-Agent Development"],
              ["bi-box-seam", "AI-Driven Product Discovery"],
              ["bi-phone", "AI-Powered App & Web Development"],
              ["bi-gear", "Full-Stack ML Product Development"],
            ].map(([icon, title], i) => (
              <div
                className="home-capability-card"
                key={i}
                onClick={() =>
                  trackEvent("capability_click", {
                    capability_name: title,
                  })
                }
              >

                <i className={`bi ${icon}`}></i>
                <h3>{title}</h3>
                <p>
                  Enterprise-grade AI solutions built with scalability, security, and governance.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI DRIVEN SOLUTION */}
      <section className="ai-driven-section">
        <div className="solutions-container ai-section-fade-in">
          <h2>AI Driven <span className="orange-text">Enterprise Solution</span></h2>
          <p>
            We build production-ready AI systems designed for enterprise scale and compliance.
          </p>

          <div className="solution-cards-grid">
            {[
              {
                icon: "bi-gear",
                title: "Agent Development",
                description: "Designing intelligent agents to enhance productivity and automate processes.",
                image: ImgA
              },
              {
                icon: "bi-lightning",
                title: "Generative AI Technologies",
                description: "Next-generation Generative (LLMs, prompt injection, multi-chain, context).",
                image: ImgB
              },
              {
                icon: "bi-chat-left-dots",
                title: "Chatbot Development",
                description: "Developing advanced conversational chatbots with RAG for improved interactions.",
                image: ImgC
              },
              {
                icon: "bi-cpu",
                title: "Core AI Technologies",
                description: "AI Pipelines & Orchestration (Airflow, Kubeflow, LLaMA, Mistral).",
                image: ImgD
              }
            ].map((solution, i) => (
              <div
                className="solution-card"
                key={i}
                onClick={() =>
                  trackEvent("solution_click", {
                    solution_name: solution.title,
                  })
                }
              >
                <img src={solution.image} alt={solution.title} className="solution-image" loading="lazy" />
                <div className="solution-content">
                  <i className={`bi ${solution.icon}`}></i>
                  <h3>{solution.title}</h3>
                  <p>{solution.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= WHY COMBAT SOLUTIONS ================= */}
      <section className="why-combat-section fade-in-section">
        <div className="why-combat-container">
          <div className="why-combat-content">
            <h2>Why <span className="orange-text">Combat Solutions?</span></h2>
            <p>
              Combat Solutions partners with enterprises to deliver secure, scalable, and production-ready technology solutions. By combining strong software engineering with practical Generative AI, we build reliable systems that integrate seamlessly, perform at scale, and deliver real business value and ensuring confident adoption, satisfied users, and long-term impact.
            </p>
          </div>
          <div className="why-combat-image">
            <div className="image-placeholder"></div>
          </div>
        </div>
      </section>

      {/* ================= PARTNERS ================= */}
      <section className="partners-section">
        <div className="partners-header">
          <h2>Trusted by <span className="orange-text">25+ IT Organizations</span></h2>
        </div>
        <div className="partners-slider-wrapper">
          <div className="partners-slider" ref={partnersSliderRef}>
            {[...partnersLogos, ...partnersLogos].map((partner, i) => (
              <div className="slider-item" key={`partner-${i}`}>
                <img
                  src={partner}
                  alt="partner"
                  loading="lazy"
                  width="120"
                  height="80"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CLIENTS TESTIMONIALS ================= */}
      <section
        className="client-section fade-in-section"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="clients-header">
          <h2>What Our Clients Say</h2>
          <p>
            Trusted by enterprises for secure, scalable, and reliable technology delivery
          </p>
        </div>

        {/* TESTIMONIALS CAROUSEL */}
        <div
          className="testimonials-carousel"
          ref={testimonialsRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="carousel-track" ref={slideTrackRef}>
            {isMobile ? (
              // Mobile: Show 1 testimonial per slide
              testimonials.map((item, index) => (
                <div className="carousel-slide" key={item.id}>
                  <div className="testimonial-card">
                    <div className="stars">
                      {[...Array(item.rating)].map((_, i) => (
                        <i key={i} className="bi bi-star-fill"></i>
                      ))}
                    </div>

                    <p className="testimonial-text">{item.text}</p>

                    <div className="client-info">
                 
                      <div className="client-details">
                        <p className="client-name">{item.name}</p>
                        <p className="client-title">
                          {item.title}, {item.company}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              // Desktop: Show 2 testimonials per slide
              Array.from({ length: totalSlides }).map((_, slideIndex) => {
                const startIndex = slideIndex * 2;
const cardsInThisSlide = getCardsInSlide(slideIndex);
const slideTestimonials = testimonials.slice(
  startIndex,
  startIndex + cardsInThisSlide
);
                
                return (
                  <div className="carousel-slide" key={slideIndex}>
                    <div className={`testimonials-grid-desktop ${
    cardsInThisSlide === 1 ? "single-column" : ""
  }`}>
                      {slideTestimonials.map((item, index) => (
                        <div className="testimonial-card" key={item.id}>
                          <div className="stars">
                            {[...Array(item.rating)].map((_, i) => (
                              <i key={i} className="bi bi-star-fill"></i>
                            ))}
                          </div>

                          <p className="testimonial-text">{item.text}</p>

                          <div className="client-info">
                            <div className="client-details">
                              <p className="client-name">{item.name}</p>
                              <p className="client-title">
                                {item.title}, {item.company}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* CAROUSEL NAVIGATION - Only show on desktop */}
        {!isMobile && (
          <div className="carousel-navigation">

            <div className="carousel-dots">
              {Array.from({ length: totalSlides }).map((_, i) => (
                <button
                  key={i}
                  className={`dot ${currentSlide === i ? "active" : ""}`}
                  onClick={() => goToSlide(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;